import { useState } from 'react';
import { Plus, Warehouse as WarehouseIcon } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import {
  useCreateWarehouseMutation, useDeleteWarehouseMutation, useWarehousesQuery,
  type Warehouse,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import {
  Alert, Badge, Button, Card, Checkbox, ConfirmDialog, EmptyState, ErrorState, Input,
  LoadingState, Modal, PageHeader, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';

export function WarehousesPage() {
  const branches = useAppSelector((state) => state.tenant.context?.branches ?? []);
  const { data, isLoading, isError, error, refetch } = useWarehousesQuery();
  const [createWarehouse, createState] = useCreateWarehouseMutation();
  const [deleteWarehouse, deleteState] = useDeleteWarehouseMutation();

  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Warehouse | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '', code: '', branchId: '', isDefault: false, isSellable: true,
  });

  const fieldErrors = apiFieldErrors(createState.error);

  const submit = async (): Promise<void> => {
    const created = await createWarehouse({
      name: form.name,
      code: form.code,
      branchId: form.branchId || undefined,
      isDefault: form.isDefault,
      isSellable: form.isSellable,
    })
      .unwrap()
      .catch(() => null);

    if (created) {
      setCreating(false);
      setForm({ name: '', code: '', branchId: '', isDefault: false, isSellable: true });
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setDeleteError(null);

    try {
      await deleteWarehouse(pendingDelete.id).unwrap();
      setPendingDelete(null);
    } catch (deletionError) {
      // The API refuses while stock remains — surface exactly why.
      setDeleteError(apiErrorMessage(deletionError, 'This warehouse could not be archived'));
    }
  };

  return (
    <>
      <PageHeader
        title="Warehouses"
        description="Every stock movement belongs to a warehouse. Balances are tracked per warehouse."
        action={
          <Can permission={P.WAREHOUSE_CREATE}>
            <Button icon={Plus} onClick={() => setCreating(true)}>New warehouse</Button>
          </Can>
        }
      />

      <Card>
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={WarehouseIcon}
            title="No warehouses yet"
            description="Create at least one warehouse before recording stock."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Warehouse</Th>
                <Th>Branch</Th>
                <Th numeric>Stock records</Th>
                <Th numeric>Bin locations</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {data?.items.map((warehouse) => (
                <tr key={warehouse.id} className="hover:bg-sunken">
                  <Td>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{warehouse.name}</p>
                      {warehouse.isDefault && <Badge tone="brand">Default</Badge>}
                    </div>
                    <p className="text-xs text-muted">{warehouse.code}</p>
                  </Td>
                  <Td className="text-secondary">{warehouse.branch?.name ?? '—'}</Td>
                  <Td numeric className="text-secondary">{warehouse._count?.stock ?? 0}</Td>
                  <Td numeric className="text-secondary">{warehouse._count?.locations ?? 0}</Td>
                  <Td>
                    {!warehouse.isActive ? (
                      <Badge tone="neutral">Inactive</Badge>
                    ) : warehouse.isSellable ? (
                      <Badge tone="success">Sellable</Badge>
                    ) : (
                      <Badge tone="warning">Non-sellable</Badge>
                    )}
                  </Td>
                  <Td className="text-right">
                    <Can permission={P.WAREHOUSE_DELETE}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setDeleteError(null);
                          setPendingDelete(warehouse);
                        }}
                        disabled={warehouse.isDefault}
                        title={warehouse.isDefault ? 'Make another warehouse the default first' : undefined}
                      >
                        Archive
                      </Button>
                    </Can>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New warehouse"
        description="A warehouse holds stock. Branches can have one or several."
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={() => void submit()} loading={createState.isLoading} disabled={!form.name || !form.code}>
              Create warehouse
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {createState.error && <Alert tone="danger">{apiErrorMessage(createState.error)}</Alert>}

          <Input
            label="Name"
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            error={fieldErrors.name}
          />
          <Input
            label="Code"
            required
            placeholder="WH-02"
            value={form.code}
            onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
            error={fieldErrors.code}
            hint="A short unique code used on documents."
          />
          <Select
            label="Branch"
            value={form.branchId}
            onChange={(event) => setForm({ ...form, branchId: event.target.value })}
            hint="Optional — a warehouse can stand alone."
          >
            <option value="">No branch</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </Select>

          <Checkbox
            label="Make this the default warehouse"
            description="New stock movements default to this warehouse."
            checked={form.isDefault}
            onChange={(event) => setForm({ ...form, isDefault: event.target.checked })}
          />
          <Checkbox
            label="Stock here is sellable"
            description="Turn this off for quarantine or damaged-goods stores."
            checked={form.isSellable}
            onChange={(event) => setForm({ ...form, isSellable: event.target.checked })}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Archive this warehouse?"
        message={
          deleteError ??
          `${pendingDelete?.name} will be archived. Its ledger history is kept and stays readable.`
        }
        confirmLabel="Archive"
        loading={deleteState.isLoading}
      />
    </>
  );
}
